/**
 * The Word snapshot the pane uploads before each Word turn. Mirrors `app/word_kit/snapshot.py`
 * (pydantic, `extra="forbid"`): field names and optionality must match exactly.
 *
 * Hashes (shared contract, see `tests/word_kit/golden/README.md`):
 * - paragraph hash = first 16 hex chars of SHA-256 over the UTF-8 bytes of `text_live`;
 * - doc_hash = first 16 hex chars of SHA-256 over the UTF-8 bytes of `${pid}\t${hash}\n` for every
 *   paragraph, in document order.
 * Text must be well-formed Unicode: lone surrogates are replaced with U+FFFD before hashing.
 */

export const SCHEMA_VERSION = 1;
export const MAX_SNAPSHOT_CHARS = 1_000_000;
export const PID_PATTERN = /^(?:p\d+|fn\d+\.\d+|en\d+\.\d+|hf\d+\.\d+)$/;

export interface TableCell {
  table: number;
  row: number;
  cell: number;
}

/** One run of `text_live`: text a pending tracked change deletes (`deleted`) or text that stays. */
export interface LiveSegment {
  text: string;
  deleted: boolean;
}

export interface SnapshotParagraph {
  pid: string;
  /** `Paragraph.text`. Word on the web includes tracked-deleted text here; `live_segments` says where. */
  text_live: string;
  text_reviewed: string;
  /**
   * `text_live` split into runs marking the text pending deletions still hold (concatenation = `text_live`,
   * at least one deleted run). Absent/null: none, or not placed exactly. Runs, not offsets (Python counts
   * code points): each side derives its own spans (`deletedSpans`).
   */
  live_segments?: LiveSegment[] | null;
  list_string?: string | null;
  style?: string | null;
  in_table?: TableCell | null;
  locked?: boolean;
  hash: string;
}

export type TrackedChangeType = "added" | "deleted" | "formatted";

export interface SnapshotTrackedChange {
  seq: number;
  pid: string;
  type: TrackedChangeType;
  author: string;
  date?: string | null;
  text: string;
}

export interface CommentReply {
  author: string;
  text: string;
}

export interface SnapshotComment {
  id: string;
  pid: string;
  quote: string;
  author: string;
  text: string;
  resolved: boolean;
  replies: CommentReply[];
}

export interface Selection {
  pid_start: string;
  pid_end: string;
  text: string;
}

export interface WordSnapshot {
  schema_version: 1;
  snapshot_id: string;
  title: string;
  paragraphs: SnapshotParagraph[];
  tracked_changes: SnapshotTrackedChange[];
  comments: SnapshotComment[];
  selection: Selection | null;
  cursor_pid: string | null;
  doc_hash: string;
  char_count: number;
  capture_ms: number;
}

const encoder = new TextEncoder();

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", encoder.encode(text));
  let hex = "";
  for (const byte of new Uint8Array(digest)) hex += byte.toString(16).padStart(2, "0");
  return hex;
}

/** Word's comment-anchor mark (U+0005): Word on the web puts one into `Paragraph.text` for every comment. */
const COMMENT_MARK = /\u0005/g;

/**
 * First 16 hex characters of SHA-256 over the UTF-8 bytes of `text` without comment-anchor marks (lone surrogates
 * must be gone already). A comment does not change a paragraph's words, so adding one never breaks its hash (B6).
 * Mirrors Python `paragraph_hash`; offsets stay in live-text coordinates, marks included.
 */
export async function paragraphHash(text: string): Promise<string> {
  return (await sha256Hex(text.replace(COMMENT_MARK, ""))).slice(0, 16);
}

/** First 16 hex characters of SHA-256 over `pid\thash\n` for every paragraph, in order. */
export async function documentHash(paragraphs: readonly Pick<SnapshotParagraph, "pid" | "hash">[]): Promise<string> {
  let joined = "";
  for (const p of paragraphs) joined += `${p.pid}\t${p.hash}\n`;
  return (await sha256Hex(joined)).slice(0, 16);
}

/** `[start, end)` UTF-16 spans of the deleted runs (adjacent runs merged). Mirrors Python `deleted_spans`. */
export function deletedSpans(segments: readonly LiveSegment[] | null | undefined): [number, number][] {
  const spans: [number, number][] = [];
  let position = 0;
  for (const segment of segments ?? []) {
    const end = position + segment.text.length;
    if (segment.deleted) {
      const last = spans[spans.length - 1];
      if (last !== undefined && last[1] === position) last[1] = end;
      else spans.push([position, end]);
    }
    position = end;
  }
  return spans;
}

/**
 * Runs for `text` with `spans` (UTF-16, any order, may overlap) marked deleted; null when there is none.
 * The reverse of `deletedSpans`.
 */
export function segmentsFromSpans(text: string, spans: readonly (readonly [number, number])[]): LiveSegment[] | null {
  const sorted = spans
    .map(([a, b]): [number, number] => [Math.max(0, a), Math.min(text.length, b)])
    .filter(([a, b]) => a < b)
    .sort((x, y) => x[0] - y[0]);
  if (sorted.length === 0) return null;
  const merged: [number, number][] = [];
  for (const span of sorted) {
    const last = merged[merged.length - 1];
    if (last !== undefined && span[0] <= last[1]) last[1] = Math.max(last[1], span[1]);
    else merged.push([span[0], span[1]]);
  }
  const segments: LiveSegment[] = [];
  let position = 0;
  for (const [a, b] of merged) {
    if (a > position) segments.push({ text: text.slice(position, a), deleted: false });
    segments.push({ text: text.slice(a, b), deleted: true });
    position = b;
  }
  if (position < text.length) segments.push({ text: text.slice(position), deleted: false });
  return segments;
}

/** `text_live` without the text pending deletions hold (the accepted view, apart from pending insertions). */
export function withoutDeleted(segments: readonly LiveSegment[]): string {
  return segments.filter((s) => !s.deleted).map((s) => s.text).join("");
}
